import {
  menu,
  type MenuConfig,
} from '@blocksuite/affine-components/context-menu';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  type PropertyConfigMenuContext,
  statsFunctions,
} from '@blocksuite/data-view';

import { EditorHostKey } from '../../context/host-context.js';
import type { RelationPropertyData } from '../relation/types.js';
import { RELATION_PROPERTY_TYPE } from '../relation/types.js';
import { ROLLUP_PROPERTY_TYPE, type RollupPropertyData } from './types.js';

export const buildRollupConfigMenu = (
  ctx: PropertyConfigMenuContext<RollupPropertyData>
): MenuConfig[] => {
  const { property } = ctx;
  const std = property.view.serviceGet(EditorHostKey)?.std;
  if (!std) return [];
  const data = property.data$.value;

  // 1. Source Relation candidates — relation columns on THIS view.
  const sourceRelationCandidates = property.view.propertiesRaw$.value.filter(
    p => p.type$.value === RELATION_PROPERTY_TYPE
  );

  const sourceRelationSubMenu = menu.subMenu({
    name: `Relation: ${
      data.sourceRelationPropertyId
        ? (sourceRelationCandidates.find(
            p => p.id === data.sourceRelationPropertyId
          )?.name$.value ?? '(removed)')
        : 'pick one'
    }`,
    options: {
      items:
        sourceRelationCandidates.length === 0
          ? [
              menu.action({
                name: 'No relation columns on this database',
                select: () => {},
              }),
            ]
          : sourceRelationCandidates.map(p =>
              menu.action({
                name: p.name$.value || 'Untitled',
                isSelected: data.sourceRelationPropertyId === p.id,
                select: () => {
                  property.dataUpdate(d => ({
                    ...d,
                    sourceRelationPropertyId: p.id,
                    targetPropertyId: null,
                    aggregationType: null,
                  }));
                },
              })
            ),
    },
  });

  // 2. Target property submenu — columns on target DB resolved via source relation.
  const sourceRelation = data.sourceRelationPropertyId
    ? sourceRelationCandidates.find(p => p.id === data.sourceRelationPropertyId)
    : null;
  const relationData = sourceRelation?.data$.value as
    | RelationPropertyData
    | undefined;

  const targetDb: DatabaseBlockModel | null = (() => {
    if (!relationData?.targetBlockId) return null;
    const docId = relationData.targetDocId ?? std.store.id;
    const doc = std.workspace.getDoc(docId);
    if (!doc || !doc.loaded) return null;
    const block = doc
      .getStore({ id: doc.id })
      .getBlock(relationData.targetBlockId)?.model;
    if (!block || block.flavour !== 'affine:database') return null;
    return block as unknown as DatabaseBlockModel;
  })();

  const targetColumns = targetDb
    ? targetDb.props.columns$.value.filter(
        c =>
          c.type !== ROLLUP_PROPERTY_TYPE && c.type !== RELATION_PROPERTY_TYPE
      )
    : [];

  const currentTargetCol = data.targetPropertyId
    ? targetColumns.find(c => c.id === data.targetPropertyId)
    : null;

  const targetPropertySubMenu = menu.subMenu({
    name: `Property: ${
      currentTargetCol?.name ?? (targetDb ? 'pick one' : 'pick relation first')
    }`,
    options: {
      items: !targetDb
        ? [
            menu.action({
              name: 'Pick a relation first',
              select: () => {},
            }),
          ]
        : targetColumns.length === 0
          ? [
              menu.action({
                name: 'Target database has no aggregatable columns',
                select: () => {},
              }),
            ]
          : targetColumns.map(c =>
              menu.action({
                name: c.name,
                isSelected: data.targetPropertyId === c.id,
                select: () => {
                  property.dataUpdate(d => ({
                    ...d,
                    targetPropertyId: c.id,
                    aggregationType: null,
                  }));
                },
              })
            ),
    },
  });

  // 3. Aggregation submenu — match stats functions by target's data type.
  const targetMeta = currentTargetCol
    ? property.view.manager.dataSource.propertyMetaGet(currentTargetCol.type)
    : null;
  const targetTypeInstance = targetMeta
    ? targetMeta.config.jsonValue.type({
        data: currentTargetCol?.data as never,
        dataSource: property.view.manager.dataSource,
      })
    : null;

  const aggregatables: { type: string; menuName: string }[] = [];

  // Virtual aggregations handled inline by RollupCell — available for ANY
  // target type (they stringify per-property via meta.config.rawValue.toString).
  if (currentTargetCol) {
    aggregatables.push(
      { type: 'show-original', menuName: 'Show original' },
      { type: 'show-unique', menuName: 'Show unique values' }
    );
  }

  for (const fn of statsFunctions) {
    if (!fn.impl) continue;
    if (
      targetTypeInstance &&
      fn.dataType.name !== 'Unknown' &&
      fn.dataType.name !== targetTypeInstance.name
    ) {
      continue;
    }
    aggregatables.push({
      type: fn.type,
      menuName: fn.menuName ?? fn.type,
    });
  }

  const currentAgg = aggregatables.find(f => f.type === data.aggregationType);

  const aggregationSubMenu = menu.subMenu({
    name: `Calculate: ${
      currentAgg?.menuName ??
      (currentTargetCol ? 'pick one' : 'pick property first')
    }`,
    options: {
      items: !currentTargetCol
        ? [menu.action({ name: 'Pick a property first', select: () => {} })]
        : aggregatables.map(fn =>
            menu.action({
              name: fn.menuName,
              isSelected: data.aggregationType === fn.type,
              select: () => {
                property.dataUpdate(d => ({
                  ...d,
                  aggregationType: fn.type,
                }));
              },
            })
          ),
    },
  });

  return [
    menu.group({
      items: [sourceRelationSubMenu, targetPropertySubMenu, aggregationSubMenu],
    }),
  ];
};
